import { useContext, Fragment } from "react";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import PipedriveAuthConfiguration from "./pipedrive/PipedriveAuthConfiguration";
import SmartHomeAuthConfiguration from "./smarthome/SmartHomeAuthConfiguration";
import SoftinmAuthConfiguration from "./softinm/SoftinmAuthConfiguration";
import DomusAuthConfiguration from "./domus/DomusAuthConfiguration";
import SalesforceAuthConfiguration from "./salesforce/SalesforceAuthConfiguration";
import BitrixAuthConfiguration from "./bitrix/BitrixAuthConfiguration";
import SimiAuthConfiguration from "./simi/SimiAuthConfiguration";
import HubspotAuthConfiguration from "./hubspot/HubspotAuthConfiguration";
import { CrmFormData } from "./types";

type AuthCrmConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
};

const AuthCrmConfiguration = ({
  formData,
  onFormDataChange,
}: AuthCrmConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const { data: crmConnection, isLoading: isLoadingConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  if (isLoadingConnection) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p>Cargando configuración...</p>
      </div>
    );
  }

  const hasConnection = !!crmConnection;
  const provider = crmConnection?.provider || formData.provider;

  if (!provider) {
    return <Fragment />;
  }

  // Route to the appropriate CRM auth component based on provider
  switch (provider) {
    case "pipedrive":
      return (
        <PipedriveAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "smarthome-rentals":
      return (
        <SmartHomeAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "softinm":
      return (
        <SoftinmAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "domus":
      return (
        <DomusAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "salesforce":
      return (
        <SalesforceAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "bitrix":
      return (
        <BitrixAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "simi":
      return (
        <SimiAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    case "hubspot":
      return (
        <HubspotAuthConfiguration
          formData={formData}
          onFormDataChange={onFormDataChange}
          hasConnection={hasConnection}
          crmConnection={crmConnection}
        />
      );
    default:
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <p className="text-gray-600">CRM provider "{provider}" no está soportado aún.</p>
        </div>
      );
  }
};

export default AuthCrmConfiguration;
